import { Injectable } from '@angular/core';
import { getLocaleNumberSymbol, NumberSymbol } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import type { EChartsOption } from 'echarts';

@Injectable({ providedIn: 'root' })
export class EChartsConfigService {

  // First 10 preserved for pixel-parity with existing low-cardinality charts (<=10 series).
  // Entries 10-41 are Tableau20 + ColorBrewer Set3 — established qualitative palettes chosen
  // for pairwise perceptual distinctness, not picked by hand. Row counts aren't bounded by the
  // PlanParam enum (tenants can add arbitrary shrinkage sub-category rows like "Meeting"/"Tardy"),
  // so this needs to comfortably cover realistic real-world row counts (~40), not just the enum size.
  static readonly COLORS = [
    '#7cb5ec', '#434348', '#90ed7d', '#f7a35c', '#8085e9',
    '#f15c80', '#e4d354', '#2b908f', '#f45b5b', '#91e8e1',
    '#1f77b4', '#aec7e8', '#ff7f0e', '#ffbb78', '#2ca02c',
    '#98df8a', '#d62728', '#ff9896', '#9467bd', '#c5b0d5',
    '#8c564b', '#c49c94', '#e377c2', '#f7b6d2', '#7f7f7f',
    '#c7c7c7', '#bcbd22', '#dbdb8d', '#17becf', '#9edae5',
    '#8dd3c7', '#ffffb3', '#bebada', '#fb8072', '#80b1d3',
    '#fdb462', '#b3de69', '#fccde5', '#d9d9d9', '#bc80bd',
    '#ccebc5', '#ffed6f'
  ];

  // Fallback beyond the curated palette (pathological row counts): vary hue via the golden
  // angle AND rotate lightness/saturation per "ring" so nearby indices don't cluster into the
  // same perceived color family the way pure hue-rotation at fixed lightness/saturation would.
  private static readonly GOLDEN_ANGLE = 137.508;
  private static readonly FALLBACK_HUES_PER_RING = 12;
  private static readonly FALLBACK_LIGHTNESS_RING = [45, 60, 75];
  private static readonly FALLBACK_SATURATION_RING = [55, 70];

  // WCAG 1.4.11 (>= 3:1 against the white chart background) — '#006DAD' is 5.53:1.
  static readonly PRIMARY_AXIS_COLOR = '#006DAD';
  static readonly SECONDARY_AXIS_COLOR = 'rgb(249,163,70)';

  /**
   * Colour for the drill-down (link-style) x-axis category labels. Must meet
   * WCAG 1.4.3 for normal text (>= 4.5:1 on the white chart background) — the
   * previous '#0094D1' was only ~3.4:1. '#006DAD' is 5.53:1 while staying a
   * recognisable link blue.
   */
  static readonly DRILLDOWN_AXIS_COLOR = '#006DAD';

  constructor(private translateSrv: TranslateService) {}

  getColor(index: number): string {
    if (index < EChartsConfigService.COLORS.length) {
      return EChartsConfigService.COLORS[index];
    }
    const i = index - EChartsConfigService.COLORS.length;
    const ring = Math.floor(i / EChartsConfigService.FALLBACK_HUES_PER_RING);
    const hue = (i * EChartsConfigService.GOLDEN_ANGLE) % 360;
    const lightness = EChartsConfigService.FALLBACK_LIGHTNESS_RING[ring % EChartsConfigService.FALLBACK_LIGHTNESS_RING.length];
    const saturation = EChartsConfigService.FALLBACK_SATURATION_RING[ring % EChartsConfigService.FALLBACK_SATURATION_RING.length];
    return `hsl(${hue.toFixed(2)}, ${saturation}%, ${lightness}%)`;
  }

  getDefaultChartOptions(locale: string): EChartsOption {
    const decimalPoint = getLocaleNumberSymbol(locale, NumberSymbol.Decimal);
    const thousandsSep = getLocaleNumberSymbol(locale, NumberSymbol.Group);

    return {
      // WCAG 4.1.2: ECharts 5 aria option sets role="img" + aria-label on the
      // canvas so screen readers can identify it. Interactive keyboard navigation
      // of data points is not supported by the library (canvas limitation).
      aria: {
        enabled: true,
        label: { enabled: true }
      },
      grid: {
        left: 70,
        right: 70,
        top: 45,
        bottom: 40,
        containLabel: true
      },
      legend: {
        type: 'plain',
        bottom: 6,
        left: 'center',
        orient: 'horizontal',
        textStyle: { fontSize: 12 }
      },
      xAxis: {
        type: 'category',
        data: [],
        show: true,
        triggerEvent: true,
        axisLabel: {
          show: true,
          color: EChartsConfigService.DRILLDOWN_AXIS_COLOR,
          fontSize: 12,
          interval: 'auto',
          hideOverlap: true
        },
        axisLine: { show: true },
        axisTick: { show: true }
      },
      yAxis: [
        {
          type: 'value',
          position: 'left',
          axisLabel: {
            color: EChartsConfigService.PRIMARY_AXIS_COLOR,
            fontSize: 12,
            formatter: (value: number) => this.formatNumber(value, locale, decimalPoint, thousandsSep)
          },
          splitLine: { show: true }
        },
        {
          type: 'value',
          position: 'right',
          axisLabel: {
            color: EChartsConfigService.SECONDARY_AXIS_COLOR,
            fontSize: 12,
            formatter: (value: number) => this.formatNumber(value, locale, decimalPoint, thousandsSep)
          },
          splitLine: { show: false }
        }
      ],
      tooltip: {
        trigger: 'item',
        enterable: false,
        transitionDuration: 0,
        formatter: (params: any) => {
          const items = Array.isArray(params) ? params : [params];
          if (!items.length) return '';
          const date = items[0].name || '';
          const lines = items
            .filter(p => p.value != null)
            .map(p => `${p.marker} ${p.seriesName} &nbsp;&nbsp;<b>${p.value}</b>`);
          if (!lines.length) return '';
          return `${date}<br>${lines.join('<br>')}`;
        },
        position: (point: number[], params: any, dom: any, rect: any, size: any) => {
          const [tipWidth, tipHeight] = size.contentSize;
          const [viewWidth, viewHeight] = size.viewSize;
          const gap = 10;
          let x = point[0] - tipWidth / 2;
          let y = point[1] - tipHeight - gap;
          // If not enough space above, position below the point
          if (y < 0) {
            y = point[1] + gap;
          }
          // Clamp horizontally
          if (x < 0) x = 0;
          if (x + tipWidth > viewWidth) x = viewWidth - tipWidth;
          return [x, y];
        }
      },
      dataZoom: [
        { type: 'inside', xAxisIndex: 0, filterMode: 'none', zoomOnMouseWheel: true, moveOnMouseMove: false, moveOnMouseWheel: false }
      ],
      series: [],
      animation: true
    };
  }

  // Mirrors ECharts' own left-to-right legend packing closely enough to size the grid area:
  // walk items in order, wrapping to a new row whenever the running row width would exceed the
  // container. Shared by the live chart (grid.bottom must grow with wrapped rows) and by
  // chart-export.service.ts's own copy for offscreen export rendering.
  countLegendRows(names: string[], containerWidth: number, fontSize = 12): number {
    const ITEM_ICON_WIDTH = 25;
    const ITEM_GAP = 10;
    let rows = 1;
    let rowWidth = 0;
    for (const name of names) {
      const itemWidth = ITEM_ICON_WIDTH + this.measureTextWidth(name, fontSize) + ITEM_GAP;
      if (rowWidth > 0 && rowWidth + itemWidth > containerWidth) {
        rows++;
        rowWidth = itemWidth;
      } else {
        rowWidth += itemWidth;
      }
    }
    return rows;
  }

  // The default grid.bottom (40) reserves space for exactly one legend row; each additional
  // wrapped row needs its own line height on top of that, or the legend and x-axis overlap.
  getLegendGridBottom(rows: number, baseBottom = 40, rowHeight = 20): number {
    return baseBottom + (rows - 1) * rowHeight;
  }

  // The rotated y-axis name is drawn nameGap pixels out from the axis line, but ECharts only
  // auto-reserves space for the numeric tick labels themselves (via grid.containLabel) - not for
  // the name sitting beyond them. A fixed nameGap that fits "2,000,000" overlaps once the range
  // grows a digit or two (e.g. "-10,000,000"), so size it to the widest label actually on the axis.
  getYAxisNameGap(values: number[], decimalPoint: string, thousandsSep: string, fontSize = 12, baseGap = 14): number {
    const finite = (values || []).filter(v => v != null && isFinite(v));
    if (!finite.length) return baseGap;
    const labels = [Math.max(...finite), Math.min(...finite)]
      .map(v => this.formatNumber(v, '', decimalPoint, thousandsSep));
    const widest = Math.max(...labels.map(label => this.measureTextWidth(label, fontSize)));
    return widest + baseGap;
  }

  private measureTextWidth(text: string, fontSize: number): number {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return text.length * fontSize * 0.6;
    ctx.font = `${fontSize}px sans-serif`;
    return ctx.measureText(text).width;
  }

  formatNumber(value: number, locale: string, decimalPoint: string, thousandsSep: string): string {
    if (value == null || isNaN(value)) return '';
    const parts = value.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
    return parts.join(decimalPoint);
  }

  buildSeries(options: {
    name: string;
    id: string;
    data: any[];
    isPrimary: boolean;
    isDraggable: boolean;
    color: string;
    decimal?: number;
    metadata?: any;
  }): any {
    return {
      name: options.name,
      id: options.id,
      type: 'line',
      yAxisIndex: options.isPrimary ? 0 : 1,
      lineStyle: {
        type: options.isPrimary ? 'solid' : 'dashed',
        color: options.color
      },
      itemStyle: { color: options.color },
      symbol: 'circle',
      symbolSize: 8,
      cursor: options.isDraggable ? 'pointer' : 'default',
      data: options.data,
      _metadata: options.metadata,
      _isDraggable: options.isDraggable,
      _decimal: options.decimal
    };
  }
}
